import React from 'react';
import { toast } from 'react-toastify';

import { RegistreTreeView } from '@mairistem/administres-utils';
import { request } from '@mairistem/tools';
import { Button, Input, Modal } from '@mairistem/composants';

import { getListeRegistres } from '@/routes';
import type { Registre } from '@/types';

interface ModalRegistreTreeViewProps {
  open: boolean;
  onClose: () => void;
  onSelect: (registre: Registre) => void;
  // true si on est dans un projet de pacs : ne remonte que les registres
  // projet (type 10), sinon les deux types (projet + en cours) sont remontés
  isProjet?: boolean;
}

const ModalRegistreTreeView = (props: ModalRegistreTreeViewProps) => {
  const { open, onClose, onSelect, isProjet } = props;

  const [registres, setRegistres] = React.useState([]);
  const [selectedRegistre, setSelectedRegistre] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    setLoading(true);
    // Debounce de 300ms pour éviter les appels API à chaque frappe
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      request
        .get(getListeRegistres(search || undefined, isProjet ? 10 : null))
        .then((response: any) => {
          setRegistres(response);
        })
        .catch((error) => {
          toast.error('Erreur lors de la récupération des registres :', error);
        })
        .finally(() => {
          setLoading(false);
        });
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [search, isProjet]);

  const handleSearchChange = React.useCallback(
    (_e: React.ChangeEvent<HTMLInputElement>, data: { value: string }) => {
      setSearch(data.value);
    },
    [],
  );

  const handleSelect = React.useCallback((item: Partial<Registre>) => {
    setSelectedRegistre(item);
  }, []);

  const handleValidate = React.useCallback(() => {
    if (!selectedRegistre) {
      return;
    }

    onSelect?.(selectedRegistre);
    setSearch('');
    onClose();
  }, [onClose, onSelect, selectedRegistre]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="small"
      data-testid="registre-tree-view-modal"
    >
      <Modal.Header>Choisir un registre</Modal.Header>
      <Modal.Content>
        <Input
          fluid
          input={{ 'data-testid': 'registre-tree-view-recherche' }}
          debounced={false}
          icon="search"
          placeholder="Rechercher..."
          value={search}
          onChange={handleSearchChange}
          style={{ marginBottom: '1rem' }}
        />
        <RegistreTreeView
          registres={registres}
          onSelect={handleSelect}
          loading={loading}
        />
      </Modal.Content>
      <Modal.Actions>
        <Button
          content="Annuler"
          icon="cancel"
          basic
          onClick={onClose}
          data-testid="registre-tree-view-annuler"
        />
        <Button
          content="Valider"
          data-testid="registre-tree-view-valider"
          icon="check"
          positive
          onClick={handleValidate}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default ModalRegistreTreeView;
